import React, { useEffect, useMemo, useState } from "react";
import Navbar from "../../components/Navbar";
import NoticeNotificationPoller from "../../components/NoticeNotificationPoller";
import { toast } from "react-hot-toast";
import Notice from "../Notice";
import { useDispatch } from "react-redux";
import { setUserData } from "../../redux/actions";
import axiosWrapper from "../../utils/AxiosWrapper";
import Timetable from "./Timetable";
import Material from "./Material";
import StudentDashboard from "./StudentDashboard";
import StudentProfileHub from "./StudentProfileHub";
import Exam from "../Exam";
import ViewMarks from "./ViewMarks";
import { useNavigate, useLocation } from "react-router-dom";
import CommunicationHub from "../../ChatWindow";
import {
  FiHome,
  FiCalendar,
  FiBookOpen,
  FiBell,
  FiLayers,
  FiAward,
  FiTarget,
  FiMessageCircle,
  FiUser,
} from "react-icons/fi";
import BedPracticeHub from "./BedPracticeHub";

const PROFILE_TABS = ["overview", "edit", "settings"];

const BED_PRACTICE_ITEM = {
  id: "bed-practice",
  label: "B.Ed practice",
  short: "B.Ed",
  icon: FiTarget,
  component: BedPracticeHub,
};

const MENU_ITEMS = [
  { id: "home", label: "Home", icon: FiHome, short: "Home", component: null },
  {
    id: "timetable",
    label: "Timetable",
    short: "Time",
    icon: FiCalendar,
    component: Timetable,
  },
  {
    id: "material",
    label: "Material",
    short: "Stuff",
    icon: FiBookOpen,
    component: Material,
  },
  { id: "notice", label: "Notice", short: "News", icon: FiBell, component: Notice },
  { id: "exam", label: "Exam", short: "Exam", icon: FiLayers, component: Exam },
  {
    id: "marks",
    label: "Marks",
    short: "Marks",
    icon: FiAward,
    component: ViewMarks,
  },
  {
    id: "chat",
    label: "Chat",
    short: "Chat",
    icon: FiMessageCircle,
    component: CommunicationHub,
  },
  {
    id: "profile",
    label: "Profile",
    short: "Me",
    icon: FiUser,
    component: null,
  },
];

const Home = () => {
  const [selectedMenu, setSelectedMenu] = useState("home");
  const [profileData, setProfileData] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [dashRefresh, setDashRefresh] = useState(0);
  /** Count from GET /bed-practice/student/sets for dashboard subtitle. */
  const [bedPracticeSetCount, setBedPracticeSetCount] = useState(0);

  const dispatch = useDispatch();
  const userToken = localStorage.getItem("userToken");
  const location = useLocation();
  const navigate = useNavigate();

  const profileInitialTab = useMemo(() => {
    const q = new URLSearchParams(location.search);
    const t = q.get("tab");
    return PROFILE_TABS.includes(t) ? t : "overview";
  }, [location.search]);

  const menuItems = useMemo(() => {
    if (!profileData) return MENU_ITEMS;
    const ix = MENU_ITEMS.findIndex((i) => i.id === "marks");
    if (ix === -1) return MENU_ITEMS;
    const copy = [...MENU_ITEMS];
    copy.splice(ix + 1, 0, BED_PRACTICE_ITEM);
    return copy;
  }, [profileData]);

  const fetchUserDetails = async () => {
    setIsLoading(true);
    try {
      toast.loading("Loading user details...");
      const response = await axiosWrapper.get(`/student/my-details`, {
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
      });
      if (response.data.success) {
        setProfileData(response.data.data);
        dispatch(setUserData(response.data.data));
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error(error);
      toast.error(
        error.response?.data?.message || "Error fetching user details"
      );
    } finally {
      setIsLoading(false);
      toast.dismiss();
    }
  };

  useEffect(() => {
    fetchUserDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, userToken]);

  /** Practice set count for home dashboard (optional; UI works without it too). */
  useEffect(() => {
    if (!profileData || !userToken) {
      setBedPracticeSetCount(0);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axiosWrapper.get("/bed-practice/student/sets", {
          headers: { Authorization: `Bearer ${userToken}` },
        });
        if (cancelled) return;
        const list =
          res.data?.success && Array.isArray(res.data.data) ? res.data.data : [];
        setBedPracticeSetCount(list.length);
      } catch {
        if (!cancelled) setBedPracticeSetCount(0);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [profileData, userToken, dashRefresh]);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const pathMenuId = urlParams.get("page") || "home";
    const validMenu = menuItems.find((item) => item.id === pathMenuId);
    setSelectedMenu(validMenu ? validMenu.id : "home");
  }, [location.pathname, location.search, profileData, menuItems, navigate]);

  const handleMenuClick = (menuId) => {
    setSelectedMenu(menuId);
    if (menuId === "profile") {
      navigate(`/student?page=profile&tab=overview`);
      return;
    }
    navigate(`/student?page=${menuId}`);
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="max-w-6xl mx-auto px-4 py-12 space-y-8">
          <div className="h-64 rounded-[1.65rem] bg-slate-200/80 dark:bg-slate-700/60 animate-pulse" />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="h-24 rounded-2xl bg-slate-200/70 dark:bg-slate-700/50 animate-pulse"
              />
            ))}
          </div>
          <div className="h-40 rounded-2xl bg-slate-200/60 dark:bg-slate-700/50 animate-pulse" />
        </div>
      );
    }

    if (selectedMenu === "profile" && profileData) {
      return (
        <StudentProfileHub
          profileData={profileData}
          onProfileUpdated={fetchUserDetails}
          initialTab={profileInitialTab}
        />
      );
    }

    if (selectedMenu === "home" && profileData) {
      return (
        <StudentDashboard
          profileData={profileData}
          navigate={navigate}
          refreshNonce={dashRefresh}
          onRefresh={() => setDashRefresh((n) => n + 1)}
          bedPracticeSetCount={bedPracticeSetCount}
        />
      );
    }

    const MenuItem = menuItems.find(
      (item) => item.id.toLowerCase() === selectedMenu.toLowerCase()
    )?.component;

    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8 pb-28 md:pb-8">
        {MenuItem && <MenuItem />}
      </div>
    );
  };

  return (
    <div className="relative min-h-screen bg-gradient-to-b from-slate-50 via-white to-indigo-50/40 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 overflow-x-hidden text-slate-900 dark:text-slate-100 transition-colors duration-200">
      <div className="pointer-events-none fixed inset-0 overflow-hidden -z-10">
        <div className="absolute -top-24 -left-24 w-[min(480px,90vw)] h-[min(480px,90vw)] rounded-full bg-violet-400/25 dark:bg-violet-500/10 blur-[100px] animate-aurora" />
        <div className="absolute top-1/3 -right-32 w-[min(420px,85vw)] h-[min(420px,85vw)] rounded-full bg-indigo-400/20 dark:bg-indigo-500/10 blur-[100px] animate-aurora [animation-delay:-5s]" />
        <div className="absolute bottom-0 left-1/4 w-[min(360px,70vw)] h-[min(360px,70vw)] rounded-full bg-emerald-300/15 dark:bg-emerald-500/10 blur-[90px] animate-aurora [animation-delay:-9s]" />
      </div>
      <Navbar />
      <NoticeNotificationPoller />
      <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-6 pb-6 md:pb-16">
        <nav
          className="sticky top-0 z-30 mb-3 md:mb-4 -mx-2 sm:mx-0 px-2 sm:px-0 py-3 bg-white/85 dark:bg-slate-900/80 md:bg-white/75 dark:md:bg-slate-900/75 backdrop-blur-xl border border-slate-200/50 dark:border-slate-700/60 shadow-soft md:rounded-2xl"
          aria-label="Student sections"
        >
          <ul className="grid grid-cols-4 sm:flex sm:flex-wrap sm:justify-center gap-2 md:gap-2.5">
            {menuItems.map((item) => {
              const selected =
                selectedMenu.toLowerCase() === item.id.toLowerCase();
              const Icon = item.icon;
              return (
                <li key={item.id} className="min-w-0">
                  <button
                    type="button"
                    onClick={() => handleMenuClick(item.id)}
                    title={item.label}
                    className={`
                      w-full flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2
                      rounded-xl px-2 sm:px-4 py-2 sm:py-2.5 text-[11px] sm:text-sm font-semibold border min-h-[3rem] sm:min-h-0
                      transition-all duration-200 active:scale-[0.97]
                      ${
                        selected
                          ? "bg-gradient-to-r from-indigo-600 via-violet-600 to-indigo-700 text-white border-transparent shadow-lg shadow-indigo-500/30 ring-2 ring-white/30"
                          : "bg-white/90 dark:bg-slate-800/90 text-slate-700 dark:text-slate-200 border-slate-200/80 dark:border-slate-600 hover:border-indigo-300/80 dark:hover:border-indigo-500 hover:bg-indigo-50/50 dark:hover:bg-slate-700/80 hover:text-indigo-800 dark:hover:text-indigo-200 shadow-sm"
                      }
                    `}
                  >
                    <Icon
                      className={`w-5 h-5 sm:w-4 sm:h-4 shrink-0 ${
                        selected ? "text-white/90" : "text-slate-500 dark:text-slate-400"
                      }`}
                      strokeWidth={2.2}
                    />
                    <span className="truncate max-w-[4.2rem] sm:max-w-none text-center leading-tight">
                      <span className="sm:hidden">{item.short}</span>
                      <span className="hidden sm:inline">{item.label}</span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {renderContent()}
      </div>
    </div>
  );
};

export default Home;
